import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatINR } from "@/lib/roles";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, Legend,
} from "recharts";
import { Loader2, Landmark, Building2, Users, Home, Droplets, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const COLORS = ["#0F2942", "#E8702A", "#0D7650", "#7C3AED", "#0891B2"];

export default function StateMISDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/dashboard/mis")
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.status === 403 ? "MIS dashboard is restricted to State Administrators, Municipal Commissioners and Auditors." : "Unable to load MIS data."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error) return <div className="max-w-lg mx-auto py-20 text-center text-slate-500" data-testid="mis-error">{error}</div>;
  if (!data) return null;

  const { statewide, bodies } = data;

  return (
    <div className="space-y-6" data-testid="mis-dashboard">
      <div>
        <div className="text-xs uppercase tracking-widest text-[#E8702A] font-semibold">Higher Authority</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <Landmark className="w-6 h-6 text-[#0F2942] dark:text-white" /> State MIS Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">Statewide performance across {statewide.municipal_bodies} Punjab municipal corporations · Powered by PMIDC.</p>
      </div>

      {/* Statewide KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Collection", value: formatINR(statewide.collected), sub: `${statewide.transactions.toLocaleString()} transactions`, icon: TrendingUp, tone: "bg-[#0D7650]/10 text-[#0D7650]" },
          { label: "Outstanding Dues", value: formatINR(statewide.outstanding), sub: "Statewide arrears", icon: Landmark, tone: "bg-[#E8702A]/10 text-[#E8702A]" },
          { label: "Properties on Record", value: statewide.properties.toLocaleString(), sub: `${statewide.water_consumers.toLocaleString()} water connections`, icon: Home, tone: "bg-[#0F2942]/10 text-[#0F2942] dark:text-slate-200" },
          { label: "Registered Citizens", value: statewide.citizens.toLocaleString(), sub: "Portal accounts", icon: Users, tone: "bg-[#0891B2]/10 text-[#0891B2]" },
        ].map((k) => (
          <Card key={k.label} className="hover-lift" data-testid={`mis-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${k.tone} mb-3`}>
                <k.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-500">{k.label}</div>
              <div className="text-2xl font-heading font-bold tabular-nums text-slate-900 dark:text-white mt-1">{k.value}</div>
              <div className="text-xs text-slate-500 mt-1">{k.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Municipal Body comparison chart */}
      <Card data-testid="mis-body-chart">
        <CardHeader><CardTitle className="text-base font-heading">Collection vs Outstanding — Municipal bodies</CardTitle></CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bodies} margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="municipal_body" fontSize={10} tickFormatter={(t) => t?.replace(" Municipal Corporation", "")} angle={-15} textAnchor="end" height={60} />
              <YAxis fontSize={11} tickFormatter={(v) => v > 100000 ? `${(v/100000).toFixed(1)}L` : (v > 1000 ? `${(v/1000).toFixed(0)}K` : v)} />
              <Tooltip formatter={(v) => formatINR(v)} />
              <Legend />
              <Bar dataKey="collected" fill="#0D7650" name="Collected" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outstanding" fill="#E8702A" name="Outstanding" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Body-wise table */}
      <Card data-testid="mis-body-table">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Building2 className="w-4 h-4" /> Municipal body performance</CardTitle>
          <Link to="/reports" className="text-xs text-[#0F2942] hover:underline">Detailed reports →</Link>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-3 px-4">Municipal Body</th>
                <th className="text-right py-3 px-4">Properties</th>
                <th className="text-right py-3 px-4">Annual Tax</th>
                <th className="text-right py-3 px-4">Collected</th>
                <th className="text-right py-3 px-4">Outstanding</th>
                <th className="text-right py-3 px-4">Efficiency</th>
                <th className="text-right py-3 px-4">Txns</th>
              </tr>
            </thead>
            <tbody>
              {bodies.map((b) => (
                <tr key={b.municipal_body} className="border-t hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3 px-4 font-medium">{b.municipal_body}</td>
                  <td className="py-3 px-4 text-right tabular-nums">{b.properties.toLocaleString()}</td>
                  <td className="py-3 px-4 text-right font-mono">{formatINR(b.annual_tax)}</td>
                  <td className="py-3 px-4 text-right font-mono text-[#0D7650]">{formatINR(b.collected)}</td>
                  <td className="py-3 px-4 text-right font-mono text-[#E8702A]">{formatINR(b.outstanding)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <div className="w-16 h-2 rounded-full bg-slate-200 overflow-hidden">
                        <div className="h-full bg-[#0F2942]" style={{ width: `${Math.min(b.collection_efficiency, 100)}%` }} />
                      </div>
                      <span className="tabular-nums text-xs">{b.collection_efficiency.toFixed(0)}%</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right tabular-nums text-xs text-slate-500">{b.transactions.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
